import React, { useCallback, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { CheckCircle, RefreshCw } from "lucide-react";
import { getRequest, postRequest } from "../../ApiFunction";
import API, { BASE_URL } from "../../Api";
import { toast } from "react-toastify";

const NotificationManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getRequest(`${BASE_URL}${API.ALL_NOTIFICATIONS}`);
      const { success, data, message } = result.data;

      if (!success) {
        toast.error(message || "Failed to fetch notifications");
        return;
      }

      // Transform the data to match the table structure
      const transformedData = data?.notifications?.map((notif, index) => ({
        id: notif._id || index + 1,
        employeeName: notif.employeeName || notif.employee?.name || "N/A",
        loanId: notif.loanId || notif.loanApplicationNumber || "N/A",
        message: notif.message || notif.notificationMessage || "N/A",
        status: notif.status || notif.deliveryStatus || "unknown",
        createdAt: notif.createdAt || notif.sentAt || new Date().toISOString(),
      })) || [];

      setRows(transformedData);
    } catch (err) {
      // If endpoint doesn't exist or has an error, show a helpful message
      if (err.response?.status === 404 || err.response?.status === 500) {
        toast.info("Notification service unavailable. Using sample data.");
        // Keep showing static data as fallback
        setRows([
          {
            id: 1,
            employeeName: "John Doe",
            loanId: "LN001",
            message: "Loan request submitted successfully.",
            status: "success",
            createdAt: "2025-09-10T12:30:00Z",
          },
          {
            id: 2,
            employeeName: "Jane Smith",
            loanId: "LN002",
            message: "Failed to send loan approval notification.",
            status: "failure",
            createdAt: "2025-09-11T09:15:00Z",
          },
          {
            id: 3,
            employeeName: "Mike Johnson",
            loanId: "LN003",
            message: "Loan closed successfully.",
            status: "success",
            createdAt: "2025-09-12T14:45:00Z",
          },
        ]);
      } else {
        toast.error(err.message || "Error fetching notifications");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleResendNotification = async (notificationId) => {
    try {
      const result = await postRequest(
        `${BASE_URL}${API.RESEND_NOTIFICATION}`,
        { notificationId }
      );
      const { success, message } = result.data;

      if (success) {
        toast.success(message || "Notification resent successfully");
        fetchNotifications(); // Refresh the list
      } else {
        toast.error(message || "Failed to resend notification");
      }
    } catch (err) {
      toast.error(err.message || "Error resending notification");
      console.error("Error resending notification:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "employeeName", headerName: "Employee", width: 180 },
    { field: "loanId", headerName: "Loan ID", width: 120 },
    { field: "message", headerName: "Message", width: 250 },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (params) => (
        <span
          style={{
            color: params.row.status === "success" ? "green" : "red",
            fontWeight: "bold",
          }}
        >
          {params.row.status}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      valueGetter: (params) =>
        new Date(params.row.createdAt).toLocaleString(),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      renderCell: (params) =>
        params.row.status === "failure" ? (
          <RefreshCw
            size={22}
            style={{ cursor: "pointer", color: "orange" }}
            onClick={() => handleResendNotification(params.row.id)}
          />
        ) : (
          <CheckCircle size={22} style={{ color: "green" }} />
        ),
    },
  ];

  const paginationModel = { page: 0, pageSize: 5 };

  return (
    <div className="container">
      <div className="row">
        <div className="col-xs-12">
          <h5 className="mb-1">Notification Management</h5>
          <p className="text-muted mb-4">
            List of all loan notifications, their status, and available actions.
          </p>
          <Paper className="custom-paper">
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              initialState={{ pagination: { paginationModel } }}
              pageSizeOptions={[5, 10]}
              sx={{ border: 0 }}
            />
          </Paper>
        </div>
      </div>
    </div>
  );
};

export default NotificationManagement;
